import { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { composeSnapshot, shareSnapshot } from "@/lib/snapshot";
import { createXRStore } from "@react-three/xr";
import { Camera, Smartphone, Sparkles } from "lucide-react";
import { PageHeader } from "@/components/layout/AppShell";
import { isFlashing, useMarket } from "@/context/MarketContext";
import { usePoll, useRx } from "@/context/AuthContext";
import { fmt } from "@/lib/format";

const ImmersiveScene = lazy(() => import("@/immersive/ImmersiveScene"));
const SYMS = ["NIFTY", "BANKNIFTY", "FINNIFTY", "SENSEX", "RELIANCE", "HDFCBANK", "ICICIBANK", "INFY"];

export default function ImmersivePage() {
  const store = useMemo(() => createXRStore({ emulate: false, hitTest: true, domOverlay: true }), []);
  const [symbol, setSymbol] = useState("NIFTY");
  const [arOk, setArOk] = useState(null);
  const { quotes, flash, testAlert } = useMarket();
  const { data } = usePoll("/segment/option-buyer", 3000);
  const q = quotes[symbol];
  const signal = data?.items.find((i) => i.symbol === symbol);
  const { me } = useRx();
  const captureRef = useRef(null);
  const [capturing, setCapturing] = useState(false);
  const capture = async () => {
    if (!captureRef.current || capturing) return;
    setCapturing(true);
    try {
      const shot = captureRef.current();
      const meta = { symbol, score: q?.score ?? 50, action: signal?.action || "WATCH", instrument: signal?.instrument || symbol, levels: signal?.levels || [], referral: me?.referral_code };
      const card = await composeSnapshot(shot, meta);
      const r = await shareSnapshot(card, meta);
      if (r === "downloaded") toast.success("Snapshot saved. WhatsApp opened, attach the image to share.");
      else if (r === "shared") toast.success("Snapshot shared");
    } catch (e) { toast.error("Could not capture snapshot"); }
    setCapturing(false);
  };

  useEffect(() => {
    if (!navigator.xr) return setArOk(false);
    navigator.xr.isSessionSupported("immersive-ar").then(setArOk).catch(() => setArOk(false));
  }, []);

  return (
    <div data-testid="immersive-page">
      <PageHeader eyebrow="Immersive Mode" title="3D Market Space" sub="Strength orb glows from red to green with conviction, spot vs futures float as price planes linked by the premium line, and particles fire when Score ≥ 75."
        right={
          <div className="flex flex-col items-start md:items-end gap-2">
            <div className="flex gap-2">
            <button onClick={capture} disabled={capturing} data-testid="snapshot-btn"
              className="inline-flex items-center gap-2 rounded-full border border-[#007AFF]/50 text-white font-bold px-5 py-2 text-sm hover:bg-[#007AFF]/10 hover:shadow-[0_0_20px_rgba(0,122,255,.4)] transition-shadow disabled:opacity-50">
              <Camera size={16} /> {capturing ? "Capturing…" : "Snapshot"}
            </button>
            <button onClick={() => store.enterAR()} disabled={!arOk} data-testid="enter-ar-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#00FF66] text-black font-bold px-5 py-2 text-sm hover:brightness-110 transition-[filter] disabled:bg-white/10 disabled:text-gray-500 disabled:cursor-not-allowed">
              <Smartphone size={16} /> View in AR
            </button>
            </div>
            {arOk === false && <span className="text-[11px] text-gray-500" data-testid="ar-unsupported">AR needs a WebXR phone browser (e.g. Chrome on Android). In AR, tap a table or desk to place the scene.</span>}
          </div>
        } />
      <div className="flex flex-wrap gap-2 mb-4" data-testid="immersive-symbols">
        <button onClick={() => testAlert(symbol)} data-testid="test-alert-btn" className="px-3 py-1.5 rounded-full text-xs border border-[#007AFF]/40 text-[#00B8FF] hover:bg-[#007AFF]/10 transition-colors">Test alert</button>
        {SYMS.map((s) => (
          <button key={s} onClick={() => setSymbol(s)} data-testid={`immersive-symbol-${s}`}
            className={`px-3 py-1.5 rounded-full text-xs font-data border transition-colors ${symbol === s ? "border-[#00FF66]/60 text-[#00FF66] bg-[#00FF66]/10" : "border-white/10 text-gray-400 hover:text-white"}`}>{s}</button>
        ))}
      </div>
      <div className="relative rounded-2xl overflow-hidden border border-white/10 h-[62vh] min-h-[420px] bg-[#050505]">
        <Suspense fallback={<div className="absolute inset-0 grid place-items-center text-sm text-gray-500" data-testid="immersive-loading">Loading 3D engine…</div>}>
          <ImmersiveScene store={store} captureRef={captureRef} onCapture={capture} flashAt={flash[symbol]} score={q?.score ?? 50} spot={q?.ltp ?? 0} fut={q?.fut ?? 0} symbol={symbol} signal={signal} />
        </Suspense>
        <div className="absolute left-4 top-4 rx-glass rounded-xl p-3 pointer-events-none" data-testid="immersive-hud">
          <div className="rx-label">{symbol}</div>
          <div className="font-data text-xl font-bold">{fmt(q?.ltp)}</div>
          <div className="font-data text-xs text-gray-400">Score <span className="text-white" data-testid="immersive-score">{q?.score?.toFixed(0) ?? "—"}</span></div>
          {q?.score >= 75 && <div className="flex items-center gap-1 text-[11px] text-[#00FF66] mt-1"><Sparkles size={12} /> Strong signal</div>}
          {isFlashing(flash[symbol]) && <div className="text-[11px] font-bold text-[#00FF66] mt-1 animate-pulse" data-testid="immersive-alert">SIGNAL ALERT</div>}
        </div>
      </div>
    </div>
  );
}
